import { useState, useEffect, useRef, createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  useSettings,
  useUpdateSettings,
  useSchedules,
  useUpsertSchedule,
  useTelegramStatus,
  useGenerateTelegramCode,
  useDisconnectTelegram,
  useTestPing,
  useLogout,
  useChangePassword,
  useClients,
  useCreateClient,
  useUpdateClient,
  useSetDefaultClient,
  useDeleteClient,
} from "../api/queries.js";
import type { Schedule, Client } from "../api/queries.js";
import { useTheme } from "../theme/ThemeProvider.js";

/* ── Save toast (confirms a save, per SundialSettings.dc.html) ── */

const SettingsToastCtx = createContext<(msg: string) => void>(() => {});
const useSettingsToast = () => useContext(SettingsToastCtx);

/** A bottom-center navy pill with an orange check, auto-dismissing. */
function SettingsToast({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  const reducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  useEffect(() => {
    const id = setTimeout(onDismiss, 2600);
    return () => clearTimeout(id);
  }, [message, onDismiss]);
  return (
    <div
      aria-live="polite"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 28,
        zIndex: 80,
        display: "flex",
        justifyContent: "center",
        padding: "0 16px",
        pointerEvents: "none",
      }}
    >
      <div
        role="status"
        style={{
          pointerEvents: "auto",
          display: "flex",
          alignItems: "center",
          gap: 11,
          background: "#1B2A4A",
          color: "#FBF8F4",
          padding: "12px 16px",
          borderRadius: 13,
          boxShadow: "0 8px 28px rgba(15,26,48,.4)",
          maxWidth: 420,
          animation: reducedMotion ? "none" : "scPop .2s ease",
        }}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FF9A4D" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: "none" }}>
          <path d="M5 12l4 4 10-10" />
        </svg>
        <span style={{ fontSize: 13.5, fontWeight: 500 }}>{message}</span>
      </div>
    </div>
  );
}

/* ── helpers ───────────────────────────────────────────── */

function SectionCard({
  children,
  highlighted,
}: {
  children: React.ReactNode;
  highlighted?: boolean;
}) {
  return (
    <div
      style={{
        background: "var(--card)",
        border: highlighted
          ? "2px solid color-mix(in srgb, var(--primary) 30%, var(--border))"
          : "1px solid var(--border)",
        borderRadius: 16,
        boxShadow: highlighted
          ? "0 1px 2px rgba(15,26,48,.04),0 8px 22px var(--glow)"
          : "0 1px 2px rgba(15,26,48,.04),0 6px 18px rgba(15,26,48,.04)",
        padding: 18,
      }}
    >
      {children}
    </div>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ font: "700 15px Sora", marginBottom: 3 }}>{children}</div>
  );
}

function SectionSubtitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 12.5, color: "var(--muted)", marginBottom: 16 }}>
      {children}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label
      style={{
        font: "600 11px/14px Sora",
        letterSpacing: ".07em",
        textTransform: "uppercase",
        color: "var(--muted2)",
        display: "block",
        marginBottom: 7,
      }}
    >
      {children}
    </label>
  );
}

function SaveButton({
  dirty,
  onClick,
  label = "Save",
}: {
  dirty: boolean;
  onClick: () => void;
  label?: string;
}) {
  const { t } = useTranslation();
  const toast = useSettingsToast();
  const [justSaved, setJustSaved] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleSave() {
    if (!dirty) return;
    onClick();
    toast(t("common.saved"));
    setJustSaved(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setJustSaved(false), 2200);
  }
  useEffect(() => () => void (timer.current && clearTimeout(timer.current)), []);

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
      {justSaved && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, font: "600 12.5px Sora", color: "var(--success)" }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12l4 4 10-10" />
          </svg>
          {t("common.saved")}
        </span>
      )}
      <button
        onClick={handleSave}
        disabled={!dirty}
        style={{
          minHeight: 40,
          padding: "0 18px",
          borderRadius: 10,
          font: "600 13px Sora",
          transition: ".14s",
          border: dirty ? "none" : "1px solid var(--border)",
          background: dirty ? "var(--primary)" : "var(--surface)",
          color: dirty ? "#fff" : "var(--muted2)",
          cursor: dirty ? "pointer" : "default",
          boxShadow: dirty ? "0 2px 10px var(--glow)" : "none",
        }}
      >
        {label}
      </button>
    </div>
  );
}

function PillButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        height: 40,
        padding: "0 15px",
        borderRadius: 10,
        cursor: "pointer",
        font: "600 13px Sora",
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        justifyContent: "center",
        transition: ".14s",
        background: active ? "var(--primary)" : "var(--card)",
        color: active ? "#fff" : "var(--muted)",
        border: active ? "1px solid var(--primary)" : "1px solid var(--border)",
        boxShadow: active ? "0 2px 9px var(--glow)" : "none",
      }}
    >
      {children}
    </button>
  );
}

/* ── Toggle Switch ──────────────────────────────────────── */

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      style={{
        width: 50,
        height: 30,
        flex: "none",
        borderRadius: 999,
        border: "none",
        cursor: "pointer",
        background: checked ? "var(--primary)" : "var(--tile-none, #E7DED1)",
        position: "relative",
        transition: ".2s",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 23 : 3,
          width: 24,
          height: 24,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,.3)",
          transition: ".2s",
        }}
      />
    </button>
  );
}

/* ── Section: Rate & billing ────────────────────────────── */

function RateSection() {
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();

  const initialized = useRef(false);
  const [tjmEuros, setTjmEuros] = useState<string>("");
  const [halfWeight, setHalfWeight] = useState<number>(0.5);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settings && !initialized.current) {
      initialized.current = true;
      setTjmEuros((settings.tjmCents / 100).toFixed(2));
      setHalfWeight(settings.halfDayWeight);
    }
  }, [settings]);

  function handleSave() {
    const parsed = parseFloat(tjmEuros.replace(",", "."));
    if (isNaN(parsed)) return;
    const tjmCents = Math.round(parsed * 100);
    updateSettings.mutate({ tjmCents, halfDayWeight: halfWeight });
    setDirty(false);
  }

  return (
    <SectionCard>
      <SectionTitle>Rate &amp; billing</SectionTitle>
      <SectionSubtitle>How a day converts to money.</SectionSubtitle>

      <FieldLabel>Daily rate (TJM)</FieldLabel>
      <div style={{ position: "relative", maxWidth: 200 }}>
        <span
          style={{
            position: "absolute",
            left: 14,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--muted)",
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          €
        </span>
        <input
          aria-label="TJM daily rate"
          inputMode="decimal"
          value={tjmEuros}
          onChange={(e) => {
            setTjmEuros(e.target.value);
            setDirty(true);
          }}
          style={{
            width: "100%",
            height: 44,
            borderRadius: 10,
            border: "1px solid var(--border)",
            background: "var(--surface)",
            color: "var(--text)",
            padding: "0 14px 0 30px",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 15,
            outline: "none",
          }}
        />
      </div>

      <FieldLabel>
        <span style={{ display: "block", marginTop: 16 }}>
          Half-day counts as
        </span>
      </FieldLabel>
      <div style={{ display: "flex", gap: 8 }}>
        <PillButton
          active={halfWeight === 0.5}
          onClick={() => {
            setHalfWeight(0.5);
            setDirty(true);
          }}
        >
          0.5 day
        </PillButton>
        <PillButton
          active={halfWeight === 0.4}
          onClick={() => {
            setHalfWeight(0.4);
            setDirty(true);
          }}
        >
          0.4 day
        </PillButton>
      </div>

      <SaveButton dirty={dirty} onClick={handleSave} label="Save rate" />
    </SectionCard>
  );
}

/* ── Section: Reminders & schedules ────────────────────── */

function RemindersSection() {
  const { data: settings } = useSettings();
  const { data: schedulesData } = useSchedules();
  const updateSettings = useUpdateSettings();
  const upsertSchedule = useUpsertSchedule();

  // Daily ping state
  const [pingTimes, setPingTimes] = useState<string[]>([]);
  const [newTime, setNewTime] = useState("19:00");
  const [pingEveryday, setPingEveryday] = useState(false);
  const [repeatEnabled, setRepeatEnabled] = useState(false);
  const [repeatEveryMinutes, setRepeatEveryMinutes] = useState(60);
  const [repeatUntil, setRepeatUntil] = useState("20:00");

  // Payment state
  const [reminderDay, setReminderDay] = useState(10);
  const [reminderTime, setReminderTime] = useState("09:00");

  const [dirty, setDirty] = useState(false);

  const paymentSched = schedulesData?.schedules?.find(
    (s: Schedule) => s.kind === "payment_reminder",
  );

  const initialized = useRef(false);
  useEffect(() => {
    if (settings && !initialized.current) {
      initialized.current = true;
      const times = settings.dailyPingTimes
        ? settings.dailyPingTimes.split(",").map((t) => t.trim()).filter(Boolean)
        : [settings.dailyPingTime || "19:00"];
      setPingTimes(times);
      setPingEveryday(settings.dailyPingEveryday ?? false);
      setRepeatEnabled(settings.repeatEnabled ?? false);
      setRepeatEveryMinutes(settings.repeatEveryMinutes ?? 60);
      setRepeatUntil(settings.repeatUntil ?? "20:00");
      setReminderDay(settings.paymentReminderDay || 10);
      setReminderTime(settings.paymentReminderTime || "09:00");
    }
  }, [settings]);

  function addTime() {
    if (!newTime) return;
    const next = Array.from(new Set([...pingTimes, newTime])).sort();
    setPingTimes(next);
    setDirty(true);
  }

  function removeTime(t: string) {
    setPingTimes(pingTimes.filter((x) => x !== t));
    setDirty(true);
  }

  function handleSave() {
    const [rh, rm] = reminderTime.split(":").map(Number);
    const paymentCron = `${rm ?? 0} ${rh ?? 9} ${reminderDay} * *`;
    upsertSchedule.mutate({
      id: paymentSched?.id,
      kind: "payment_reminder",
      cron: paymentCron,
      enabled: true,
    });
    updateSettings.mutate({
      dailyPingTimes: pingTimes.join(","),
      dailyPingEveryday: pingEveryday,
      repeatEnabled,
      repeatEveryMinutes,
      repeatUntil,
      paymentReminderDay: reminderDay,
      paymentReminderTime: reminderTime,
    });
    setDirty(false);
  }

  const segStyle = (active: boolean) => ({
    textAlign: "center" as const,
    padding: "10px 16px",
    borderRadius: 11,
    cursor: "pointer",
    font: "600 13px Sora",
    transition: ".14s",
    background: active ? "var(--primary)" : "var(--surface)",
    color: active ? "#fff" : "var(--text)",
    border: active ? "1px solid var(--primary)" : "1px solid var(--border)",
    boxShadow: active ? "0 2px 9px var(--glow)" : "none",
  });

  return (
    <SectionCard highlighted>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          font: "700 15px Sora",
          marginBottom: 3,
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--primary)"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7.5V12l3 2" />
        </svg>
        Reminders &amp; schedules
      </div>
      <SectionSubtitle>When the Telegram bot nudges you.</SectionSubtitle>

      {/* Daily reminders */}
      <FieldLabel>Daily reminder times</FieldLabel>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {pingTimes.map((t) => (
          <div
            key={t}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              padding: "4px 10px",
              fontSize: 13,
              fontFamily: "'JetBrains Mono', monospace",
            }}
          >
            {t}
            <button
              aria-label={`Remove ${t}`}
              onClick={() => removeTime(t)}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--muted)",
                fontSize: 15,
                lineHeight: 1,
                padding: 0,
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14 }}>
        <input
          aria-label="Daily ping time"
          type="time"
          value={newTime}
          onChange={(e) => setNewTime(e.target.value)}
          style={{
            height: 40,
            borderRadius: 10,
            border: "1px solid var(--border)",
            background: "var(--surface)",
            color: "var(--text)",
            padding: "0 12px",
            fontSize: 14,
            outline: "none",
          }}
        />
        <button
          onClick={addTime}
          style={{
            height: 40,
            padding: "0 14px",
            borderRadius: 10,
            border: "1px solid var(--primary)",
            background: "var(--primary)",
            color: "#fff",
            font: "600 13px Sora",
            cursor: "pointer",
          }}
        >
          Add time
        </button>
      </div>

      {/* Every day vs working days */}
      <FieldLabel>Schedule</FieldLabel>
      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        <button onClick={() => { setPingEveryday(false); setDirty(true); }} style={segStyle(!pingEveryday)}>
          Working days
        </button>
        <button onClick={() => { setPingEveryday(true); setDirty(true); }} style={segStyle(pingEveryday)}>
          Every day
        </button>
      </div>

      {/* Auto-repeat */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: repeatEnabled ? 10 : 14,
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Auto-repeat</div>
          <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 2 }}>
            Re-ping if the day is still not logged.
          </div>
        </div>
        <Toggle
          checked={repeatEnabled}
          label="Toggle auto-repeat"
          onChange={(v) => { setRepeatEnabled(v); setDirty(true); }}
        />
      </div>
      {repeatEnabled && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 13, color: "var(--muted)" }}>Every</span>
            <input
              aria-label="Repeat every minutes"
              type="number"
              min={5}
              max={720}
              step={5}
              value={repeatEveryMinutes}
              onChange={(e) => { setRepeatEveryMinutes(Number(e.target.value)); setDirty(true); }}
              style={{
                width: 72,
                height: 40,
                borderRadius: 10,
                border: "1px solid var(--border)",
                background: "var(--surface)",
                color: "var(--text)",
                padding: "0 12px",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 15,
                outline: "none",
              }}
            />
            <span style={{ fontSize: 13, color: "var(--muted)" }}>min until</span>
            <input
              aria-label="Repeat until time"
              type="time"
              value={repeatUntil}
              onChange={(e) => { setRepeatUntil(e.target.value); setDirty(true); }}
              style={{
                height: 40,
                borderRadius: 10,
                border: "1px solid var(--border)",
                background: "var(--surface)",
                color: "var(--text)",
                padding: "0 12px",
                fontSize: 14,
                outline: "none",
              }}
            />
          </div>
          <div style={{ fontSize: 12, color: "var(--muted)", width: "100%" }}>
            If the day still isn&apos;t logged, remind again every {repeatEveryMinutes} min until {repeatUntil}.
          </div>
        </div>
      )}

      {/* Payment reminder */}
      <div style={{ marginTop: 4 }}>
        <FieldLabel>Payment reminder day (1–28)</FieldLabel>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <input
            aria-label="Payment reminder day"
            type="number"
            min={1}
            max={28}
            value={reminderDay}
            onChange={(e) => {
              setReminderDay(Number(e.target.value));
              setDirty(true);
            }}
            style={{
              width: 72,
              height: 40,
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "var(--surface)",
              color: "var(--text)",
              padding: "0 12px",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 15,
              outline: "none",
            }}
          />
          <input
            aria-label="Payment reminder time"
            type="time"
            value={reminderTime}
            onChange={(e) => {
              setReminderTime(e.target.value);
              setDirty(true);
            }}
            style={{
              height: 40,
              borderRadius: 10,
              border: "1px solid var(--border)",
              background: "var(--surface)",
              color: "var(--text)",
              padding: "0 12px",
              fontSize: 14,
              outline: "none",
            }}
          />
        </div>
      </div>

      <SaveButton dirty={dirty} onClick={handleSave} label="Save schedules" />
    </SectionCard>
  );
}

/* ── Section: Calendar rules ────────────────────────────── */

function CalendarSection() {
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();

  const [frHolidays, setFrHolidays] = useState(true);
  const [dirty, setDirty] = useState(false);
  const calInitialized = useRef(false);

  useEffect(() => {
    if (settings && !calInitialized.current) {
      calInitialized.current = true;
      setFrHolidays(settings.frHolidaysEnabled);
    }
  }, [settings]);

  function handleSave() {
    updateSettings.mutate({ frHolidaysEnabled: frHolidays });
    setDirty(false);
  }

  return (
    <SectionCard>
      <SectionTitle>Calendar rules</SectionTitle>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginTop: 14,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            French public holidays
          </div>
          <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 2 }}>
            Auto-mark FR holidays on the heatmap &amp; expected days.
          </div>
        </div>
        <Toggle
          checked={frHolidays}
          label="Toggle French public holidays"
          onChange={(v) => {
            setFrHolidays(v);
            setDirty(true);
          }}
        />
      </div>
      <SaveButton dirty={dirty} onClick={handleSave} />
    </SectionCard>
  );
}

/* ── Section: Integrations (Telegram) ───────────────────── */

function IntegrationsSection() {
  const { data: statusData } = useTelegramStatus();
  const connected = statusData?.connected ?? false;
  const botUsername = statusData?.botUsername ?? null;
  const generateCode = useGenerateTelegramCode();
  const disconnect = useDisconnectTelegram();
  const testPing = useTestPing();
  const [pendingCode, setPendingCode] = useState<string | null>(null);
  const [testMsg, setTestMsg] = useState<string | null>(null);

  async function handleTestPing() {
    setTestMsg(null);
    const { result } = await testPing.mutateAsync();
    setTestMsg(
      result === "sent"
        ? "✅ Test reminder sent — check Telegram."
        : result === "not-connected"
          ? "⚠️ Connect Telegram first."
          : "⚠️ Bot is not running on the server.",
    );
  }

  async function handleConnect() {
    const result = await generateCode.mutateAsync();
    setPendingCode(result.code);
  }

  function handleDisconnect() {
    disconnect.mutate();
    setPendingCode(null);
  }

  return (
    <SectionCard>
      <SectionTitle>Integrations</SectionTitle>

      <div
        style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}
      >
        <div
          style={{
            width: 42,
            height: 42,
            borderRadius: 12,
            background: "var(--surface)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: "none",
            color: "var(--primary)",
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
            <path d="M21.9 4.3l-3.2 15.1c-.2 1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.3-4.9 9-8.1c.4-.3-.1-.5-.6-.2L6.4 13.3l-4.8-1.5c-1-.3-1-1 .2-1.5L20.6 3c.9-.3 1.6.2 1.3 1.3z" />
          </svg>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Telegram bot</div>
          <div
            style={{
              fontSize: 12.5,
              color: connected ? "var(--success, #3F9C6D)" : "var(--muted2)",
              marginTop: 1,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: connected
                  ? "var(--success, #3F9C6D)"
                  : "var(--muted2)",
              }}
            />
            {connected ? "Connected" : "Not connected"}
          </div>
        </div>

        <button
          onClick={connected ? handleDisconnect : handleConnect}
          disabled={generateCode.isPending || disconnect.isPending}
          style={{
            height: 38,
            padding: "0 15px",
            border: connected
              ? "1px solid var(--border)"
              : "1px solid var(--primary)",
            borderRadius: 9,
            background: connected ? "none" : "var(--primary)",
            color: connected ? "var(--muted)" : "#fff",
            font: "600 12.5px Sora",
            cursor: "pointer",
            flex: "none",
          }}
        >
          {connected ? "Disconnect" : "Connect"}
        </button>
      </div>

      {!connected && pendingCode && (
        <div
          style={{
            marginTop: 12,
            padding: "12px 14px",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 10,
            fontSize: 12.5,
            color: "var(--muted)",
          }}
        >
          {botUsername && (
            <a
              href={`https://t.me/${botUsername}?start=${pendingCode}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                height: 42,
                borderRadius: 10,
                background: "var(--primary)",
                color: "#fff",
                font: "600 13px Sora",
                textDecoration: "none",
                marginBottom: 10,
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M21.9 4.3l-3.2 15.1c-.2 1-.9 1.3-1.8.8l-4.9-3.6-2.4 2.3c-.3.3-.5.5-1 .5l.3-4.9 9-8.1c.4-.3-.1-.5-.6-.2L6.4 13.3l-4.8-1.5c-1-.3-1-1 .2-1.5L20.6 3c.9-.3 1.6.2 1.3 1.3z" />
              </svg>
              Open in Telegram
            </a>
          )}
          {botUsername ? "Or send" : "To connect, send"}{" "}
          <strong style={{ color: "var(--text)", fontFamily: "'JetBrains Mono', monospace" }}>
            /start {pendingCode}
          </strong>{" "}
          to{" "}
          <strong style={{ color: "var(--primary)" }}>
            {botUsername ? `@${botUsername}` : "the Sundial bot"}
          </strong>{" "}
          on Telegram. The code expires after 10 minutes.
        </div>
      )}

      {connected && (
        <div
          style={{
            marginTop: 12,
            padding: "12px 14px",
            background: "var(--surface)",
            border: "1px solid var(--border)",
            borderRadius: 10,
            fontSize: 12.5,
            color: "var(--muted)",
          }}
        >
          Connected. The bot will deliver your daily log prompts here.
          <button
            onClick={handleTestPing}
            disabled={testPing.isPending}
            style={{
              marginTop: 10,
              height: 38,
              width: "100%",
              border: "1px solid var(--primary)",
              borderRadius: 9,
              background: "none",
              color: "var(--primary)",
              font: "600 12.5px Sora",
              cursor: "pointer",
            }}
          >
            {testPing.isPending ? "Sending…" : "Send a test reminder now"}
          </button>
          {testMsg && (
            <div style={{ marginTop: 8, color: "var(--text)" }}>{testMsg}</div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

/* ── Section: Appearance ────────────────────────────────── */

function AppearanceSection() {
  const { theme, toggle } = useTheme();
  const updateSettings = useUpdateSettings();

  // local mode: "light" | "dark" | "system"
  const [mode, setMode] = useState<"light" | "dark" | "system">("system");

  function setThemeMode(m: "light" | "dark" | "system") {
    setMode(m);
    if (m !== "system") {
      // Apply via ThemeProvider toggle if needed
      if ((m === "dark" && theme !== "dark") || (m === "light" && theme !== "light")) {
        toggle();
      }
    }
    updateSettings.mutate({ theme: m });
  }

  const pill = (m: "light" | "dark" | "system") => ({
    flex: 1,
    height: 46,
    borderRadius: 11,
    cursor: "pointer",
    font: "600 13px Sora",
    display: "flex" as const,
    alignItems: "center" as const,
    justifyContent: "center" as const,
    gap: 7,
    transition: ".14s",
    background: mode === m ? "var(--primary)" : "var(--surface)",
    color: mode === m ? "#fff" : "var(--muted)",
    border:
      mode === m ? "1px solid var(--primary)" : "1px solid var(--border)",
    boxShadow: mode === m ? "0 2px 9px var(--glow)" : "none",
  });

  return (
    <SectionCard>
      <SectionTitle>Appearance</SectionTitle>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button onClick={() => setThemeMode("light")} style={pill("light")}>
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="4" />
            <path d="M12 3v1.5M12 19.5V21M5 5l1 1M18 18l1 1M3 12h1.5M19.5 12H21M5 19l1-1M18 6l1-1" />
          </svg>
          Daylight
        </button>
        <button onClick={() => setThemeMode("dark")} style={pill("dark")}>
          <svg
            width="17"
            height="17"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12.8A8.5 8.5 0 1 1 11.2 3a6.6 6.6 0 0 0 9.8 9.8Z" />
          </svg>
          Night
        </button>
        <button onClick={() => setThemeMode("system")} style={pill("system")}>
          System
        </button>
      </div>
    </SectionCard>
  );
}

/* ── Section: URSSAF ────────────────────────────────────── */

function UrssafSection() {
  const { t } = useTranslation();
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();

  const initialized = useRef(false);
  const [enabled, setEnabled] = useState(false);
  const [ratePct, setRatePct] = useState<string>("22");
  const [ceilingEuros, setCeilingEuros] = useState<string>("83600.00");
  const [vatFranchiseEuros, setVatFranchiseEuros] = useState<string>("27000.00");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settings && !initialized.current) {
      initialized.current = true;
      setEnabled(settings.urssafEnabled ?? false);
      setRatePct(settings.urssafRatePct == null ? "" : String(settings.urssafRatePct));
      setCeilingEuros(((settings.microCeilingCents ?? 8360000) / 100).toFixed(2));
      setVatFranchiseEuros(((settings.vatFranchiseCents ?? 2700000) / 100).toFixed(2));
    }
  }, [settings]);

  function handleSave() {
    // Blank clears the override so the statutory schedule decides the rate per
    // period. 0 would mean "no cotisations", which is a different claim.
    const trimmedRate = ratePct.trim();
    const parsedRate = trimmedRate === "" ? null : parseFloat(trimmedRate.replace(",", "."));
    if (parsedRate !== null && isNaN(parsedRate)) return;
    const parsedCeiling = parseFloat(ceilingEuros.replace(",", "."));
    if (isNaN(parsedCeiling)) return;
    const parsedVat = parseFloat(vatFranchiseEuros.replace(",", "."));
    if (isNaN(parsedVat)) return;
    updateSettings.mutate({
      urssafEnabled: enabled,
      urssafRatePct: parsedRate,
      microCeilingCents: Math.round(parsedCeiling * 100),
      vatFranchiseCents: Math.round(parsedVat * 100),
    });
    setDirty(false);
  }

  const inputStyle = {
    width: "100%",
    height: 44,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    padding: "0 14px 0 30px",
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 15,
    outline: "none",
  };

  return (
    <SectionCard>
      <SectionTitle>{t("urssaf.title")}</SectionTitle>
      <SectionSubtitle>Cotisations et plafond micro-entreprise.</SectionSubtitle>

      {/* Toggle */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{t("urssaf.enabledLabel")}</div>
          <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 2 }}>
            Affiche les indicateurs URSSAF sur le tableau de bord.
          </div>
        </div>
        <Toggle
          checked={enabled}
          label={t("urssaf.enabledLabel")}
          onChange={(v) => {
            setEnabled(v);
            setDirty(true);
          }}
        />
      </div>

      {/* Rate % */}
      <FieldLabel>{t("urssaf.rateLabel")}</FieldLabel>
      <div style={{ position: "relative", maxWidth: 200, marginBottom: 14 }}>
        <span
          style={{
            position: "absolute",
            left: 14,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--muted)",
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          %
        </span>
        <input
          aria-label={t("urssaf.rateLabel")}
          inputMode="decimal"
          step={0.1}
          value={ratePct}
          onChange={(e) => {
            setRatePct(e.target.value);
            setDirty(true);
          }}
          style={inputStyle}
        />
      </div>

      {/* Micro ceiling € */}
      <FieldLabel>{t("urssaf.ceilingLabel")}</FieldLabel>
      <div style={{ position: "relative", maxWidth: 200, marginBottom: 14 }}>
        <span
          style={{
            position: "absolute",
            left: 14,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--muted)",
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          €
        </span>
        <input
          aria-label={t("urssaf.ceilingLabel")}
          inputMode="decimal"
          value={ceilingEuros}
          onChange={(e) => {
            setCeilingEuros(e.target.value);
            setDirty(true);
          }}
          style={inputStyle}
        />
      </div>

      {/* VAT franchise € */}
      <FieldLabel>{t("urssaf.vatThresholdLabel")}</FieldLabel>
      <div style={{ position: "relative", maxWidth: 200, marginBottom: 4 }}>
        <span
          style={{
            position: "absolute",
            left: 14,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--muted)",
            fontFamily: "'JetBrains Mono', monospace",
          }}
        >
          €
        </span>
        <input
          aria-label={t("urssaf.vatThresholdLabel")}
          inputMode="decimal"
          value={vatFranchiseEuros}
          onChange={(e) => {
            setVatFranchiseEuros(e.target.value);
            setDirty(true);
          }}
          style={inputStyle}
        />
      </div>

      <SaveButton dirty={dirty} onClick={handleSave} label="Save URSSAF" />
    </SectionCard>
  );
}

/* ── Section: Impôt sur le revenu ───────────────────────── */

const SITUATIONS = ["celibataire", "marie", "pacse", "divorce", "veuf"] as const;

const REGIMES = [
  ["bnc", "tax.regimeBnc"],
  ["bic_services", "tax.regimeBicServices"],
  ["bic_vente", "tax.regimeBicVente"],
] as const;

/* ── Section: Google Sheets sync ────────────────────────── */

/**
 * Mirror the ledger into an accountant's Google Sheet.
 *
 * The service-account key is WRITE-ONLY: the server never sends it back, so the
 * field never shows a value — it only accepts a new one. State is conveyed by
 * `sheetsConnectedAs` (the robot's email) instead, so the screen can say
 * "connected as X" without ever holding the secret.
 */
/* ── Section: Clients ───────────────────────────────────── */

const clientField: React.CSSProperties = {
  width: "100%",
  height: 40,
  borderRadius: 9,
  border: "1px solid var(--border)",
  background: "var(--surface)",
  color: "var(--text)",
  padding: "0 12px",
  fontSize: 14,
  outline: "none",
};

function ClientEditor({ client, isDefault, onClose }: { client: Client; isDefault: boolean; onClose?: () => void }) {
  const { t } = useTranslation();
  const updateClient = useUpdateClient();
  const [name, setName] = useState(client.name);
  const [tjmEuros, setTjmEuros] = useState((client.tjmCents / 100).toFixed(2));
  const [siret, setSiret] = useState(client.siret);
  const [address, setAddress] = useState(client.address);
  const [dirty, setDirty] = useState(false);

  function save() {
    const parsed = parseFloat(tjmEuros.replace(",", "."));
    updateClient.mutate({
      id: client.id,
      name: name.trim(),
      siret: siret.trim(),
      address: address.trim(),
      ...(isNaN(parsed) ? {} : { tjmCents: Math.round(parsed * 100) }),
    });
    setDirty(false);
    onClose?.();
  }

  const on = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setter(e.target.value);
    setDirty(true);
  };

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 12,
        padding: 14,
        marginBottom: 10,
        background: client.archived ? "var(--surface)" : "var(--card)",
        opacity: client.archived ? 0.7 : 1,
      }}
    >
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 2, minWidth: 160 }}>
          <FieldLabel>{t("clients.name")}</FieldLabel>
          <input aria-label={t("clients.name")} value={name} onChange={on(setName)} style={clientField} />
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <FieldLabel>{t("clients.tjm")}</FieldLabel>
          <input
            aria-label={t("clients.tjm")}
            inputMode="decimal"
            value={tjmEuros}
            onChange={on(setTjmEuros)}
            style={{ ...clientField, fontFamily: "'JetBrains Mono', monospace" }}
          />
        </div>
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
        <div style={{ flex: 1, minWidth: 120 }}>
          <FieldLabel>{t("clients.siret")}</FieldLabel>
          <input aria-label={t("clients.siret")} value={siret} onChange={on(setSiret)} style={clientField} />
        </div>
        <div style={{ flex: 2, minWidth: 160 }}>
          <FieldLabel>{t("clients.address")}</FieldLabel>
          <input aria-label={t("clients.address")} value={address} onChange={on(setAddress)} style={clientField} />
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="btn secondary"
            style={{ height: 36, padding: "0 14px", fontSize: 13 }}
          >
            {t("common.cancel")}
          </button>
        )}
        {!isDefault && (
          <button
            type="button"
            onClick={() => updateClient.mutate({ id: client.id, archived: !client.archived })}
            className="btn secondary"
            style={{ height: 36, padding: "0 14px", fontSize: 13 }}
          >
            {client.archived ? t("clients.unarchive") : t("clients.archive")}
          </button>
        )}
        <button
          type="button"
          onClick={save}
          disabled={!dirty}
          className="btn primary"
          style={{ height: 36, padding: "0 14px", fontSize: 13, opacity: dirty ? 1 : 0.5 }}
        >
          {t("common.save")}
        </button>
      </div>
    </div>
  );
}

/** A compact client row: avatar, name, mono meta, edit + archive icon buttons. */
function ClientRow({ client, isDefault, onEdit }: { client: Client; isDefault: boolean; onEdit: () => void }) {
  const { t } = useTranslation();
  const updateClient = useUpdateClient();
  const setDefault = useSetDefaultClient();
  const deleteClient = useDeleteClient();
  const toast = useSettingsToast();
  const initial = isDefault ? "★" : (client.name.trim()[0] ?? "?").toUpperCase();
  const meta =
    `${(client.tjmCents / 100).toLocaleString("fr-FR")} €/day` +
    (client.siret ? ` · SIRET ${client.siret}` : "");
  const iconBtn: React.CSSProperties = {
    width: 40,
    height: 40,
    flex: "none",
    border: "1px solid var(--border)",
    borderRadius: 9,
    background: "var(--surface)",
    color: "var(--muted)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "12px 14px",
        border: "1px solid var(--border)",
        borderRadius: 12,
        background: "var(--card)",
        marginBottom: 9,
        opacity: client.archived ? 0.6 : 1,
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          flex: "none",
          borderRadius: 9,
          background: isDefault ? "color-mix(in srgb, var(--primary) 14%, transparent)" : "var(--surface)",
          color: isDefault ? "var(--primary-deep, var(--primary))" : "var(--text)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          font: "700 13px Sora",
        }}
      >
        {initial}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>
          {client.name}
          {client.archived && (
            <span style={{ fontSize: 11, color: "var(--muted2)", fontWeight: 500 }}> · {t("clients.archivedTag")}</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: "var(--muted)", fontFamily: "'JetBrains Mono', monospace" }}>{meta}</div>
      </div>
      {isDefault ? (
        <span style={{ font: "600 10px Sora", letterSpacing: ".05em", textTransform: "uppercase", color: "var(--muted2)", background: "var(--card)", border: "1px solid var(--border)", padding: "4px 8px", borderRadius: 7, whiteSpace: "nowrap" }}>
          {t("clients.defaultTag")}
        </span>
      ) : (
        <button onClick={() => setDefault.mutate(client.id)} aria-label={t("clients.setDefault")} title={t("clients.setDefault")} style={iconBtn}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3l2.5 5.5 6 .5-4.5 4 1.4 6L12 15.8 6.6 19l1.4-6L3.5 9l6-.5z" />
          </svg>
        </button>
      )}
      <button onClick={onEdit} aria-label={t("clients.editAria")} style={iconBtn}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3z" />
        </svg>
      </button>
      {!isDefault && (
        <button
          onClick={() => {
            updateClient.mutate({ id: client.id, archived: !client.archived });
            toast(t(client.archived ? "clients.unarchivedToast" : "clients.archivedToast", { name: client.name }));
          }}
          aria-label={client.archived ? t("clients.unarchive") : t("clients.archive")}
          style={iconBtn}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="4" rx="1" />
            <path d="M5 9v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V9M10 13h4" />
          </svg>
        </button>
      )}
      {!isDefault && (
        <button
          onClick={() => {
            deleteClient.mutate(client.id, {
              onError: () => toast(t("clients.deleteInUse", { name: client.name })),
              onSuccess: () => toast(t("clients.deletedToast", { name: client.name })),
            });
          }}
          aria-label={t("clients.delete")}
          title={t("clients.delete")}
          style={{ ...iconBtn, color: "var(--danger)" }}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 7h16M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M6 7l1 13a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-13" />
          </svg>
        </button>
      )}
    </div>
  );
}

function ClientsSection() {
  const { t } = useTranslation();
  const { data } = useClients();
  const { data: settings } = useSettings();
  const createClient = useCreateClient();
  const toast = useSettingsToast();
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [newTjm, setNewTjm] = useState("");

  function add() {
    const parsed = parseFloat(newTjm.replace(",", "."));
    if (newName.trim() === "" || isNaN(parsed)) return;
    createClient.mutate({ name: newName.trim(), tjmCents: Math.round(parsed * 100) });
    toast(t("clients.added", { name: newName.trim() }));
    setNewName("");
    setNewTjm("");
    setAdding(false);
  }

  const clients = data?.clients ?? [];
  const defaultId = settings?.defaultClientId ?? "client-default";
  // Default client first, then other active clients, then archived. Its rate is
  // read-only here (edit it in Rate & billing); it can't be archived or deleted.
  const ordered = [...clients].sort((a, b) => {
    if (a.id === defaultId) return -1;
    if (b.id === defaultId) return 1;
    if (a.archived !== b.archived) return a.archived ? 1 : -1;
    return 0;
  });

  return (
    <SectionCard>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, marginBottom: 3 }}>
        <SectionTitle>{t("clients.title")}</SectionTitle>
        <span style={{ fontSize: 12, color: "var(--muted2)", fontFamily: "'JetBrains Mono', monospace" }}>
          {t("clients.count", { count: clients.length })}
        </span>
      </div>
      <SectionSubtitle>{t("clients.subtitle")}</SectionSubtitle>

      {ordered.map((c) =>
        editingId === c.id ? (
          <ClientEditor key={c.id} client={c} isDefault={c.id === defaultId} onClose={() => setEditingId(null)} />
        ) : (
          <ClientRow key={c.id} client={c} isDefault={c.id === defaultId} onEdit={() => setEditingId(c.id)} />
        ),
      )}

      {adding ? (
        <div style={{ border: "1px dashed var(--border)", borderRadius: 12, padding: 14, marginTop: 4 }}>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 2, minWidth: 160 }}>
              <FieldLabel>{t("clients.name")}</FieldLabel>
              <input
                aria-label={t("clients.newName")}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Atlas SAS"
                style={clientField}
              />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <FieldLabel>{t("clients.tjm")}</FieldLabel>
              <input
                aria-label={t("clients.newTjm")}
                inputMode="decimal"
                value={newTjm}
                onChange={(e) => setNewTjm(e.target.value)}
                placeholder="600"
                style={{ ...clientField, fontFamily: "'JetBrains Mono', monospace" }}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
            <button type="button" onClick={() => setAdding(false)} className="btn secondary" style={{ height: 36, padding: "0 14px", fontSize: 13 }}>
              {t("common.cancel")}
            </button>
            <button type="button" onClick={add} className="btn primary" style={{ height: 36, padding: "0 14px", fontSize: 13 }}>
              {t("clients.add")}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          style={{
            width: "100%",
            height: 44,
            marginTop: 4,
            border: "1px dashed color-mix(in srgb, var(--primary) 40%, var(--border))",
            borderRadius: 11,
            background: "none",
            color: "var(--primary-deep)",
            font: "600 13.5px Sora",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
          }}
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 5v14M5 12h14" />
          </svg>
          {t("clients.add")}
        </button>
      )}
    </SectionCard>
  );
}

/* ── Section: Identity (relevé PDF header) ──────────────── */

/**
 * The freelance's own details, printed at the top of the relevé d'activité PDF.
 * Without these the statement reads as "Sundial's PDF" rather than the
 * freelance's own document.
 */
function IdentitySection() {
  const { t } = useTranslation();
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();

  const initialized = useRef(false);
  const [name, setName] = useState("");
  const [siret, setSiret] = useState("");
  const [address, setAddress] = useState("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settings && !initialized.current) {
      initialized.current = true;
      setName(settings.bizName ?? "");
      setSiret(settings.bizSiret ?? "");
      setAddress(settings.bizAddress ?? "");
    }
  }, [settings]);

  function handleSave() {
    updateSettings.mutate({ bizName: name.trim(), bizSiret: siret.trim(), bizAddress: address.trim() });
    setDirty(false);
  }

  const field: React.CSSProperties = {
    width: "100%",
    height: 44,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    padding: "0 14px",
    fontSize: 14,
    outline: "none",
  };

  return (
    <SectionCard>
      <SectionTitle>{t("identity.title")}</SectionTitle>
      <SectionSubtitle>{t("identity.subtitle")}</SectionSubtitle>

      <FieldLabel>{t("identity.nameLabel")}</FieldLabel>
      <input
        aria-label={t("identity.nameLabel")}
        value={name}
        placeholder="Camille Rousseau"
        onChange={(e) => {
          setName(e.target.value);
          setDirty(true);
        }}
        style={{ ...field, marginBottom: 14 }}
      />
      <FieldLabel>{t("identity.siretLabel")}</FieldLabel>
      <input
        aria-label={t("identity.siretLabel")}
        value={siret}
        placeholder="902 345 678 00021"
        onChange={(e) => {
          setSiret(e.target.value);
          setDirty(true);
        }}
        style={{ ...field, marginBottom: 14 }}
      />
      <FieldLabel>{t("identity.addressLabel")}</FieldLabel>
      <input
        aria-label={t("identity.addressLabel")}
        value={address}
        placeholder="14 rue des Lilas · 69003 Lyon"
        onChange={(e) => {
          setAddress(e.target.value);
          setDirty(true);
        }}
        style={field}
      />

      <SaveButton dirty={dirty} onClick={handleSave} label={t("identity.save")} />
    </SectionCard>
  );
}

/* ── Section: Google Sheets sync ────────────────────────── */

function SheetsSection() {
  const { t } = useTranslation();
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();

  const initialized = useRef(false);
  const [autoSync, setAutoSync] = useState(false);
  const [spreadsheetId, setSpreadsheetId] = useState("");
  const [newKey, setNewKey] = useState(""); // never pre-filled — the key is write-only
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settings && !initialized.current) {
      initialized.current = true;
      setAutoSync(settings.sheetsAutoSync ?? false);
      setSpreadsheetId(settings.sheetsSpreadsheetId ?? "");
    }
  }, [settings]);

  function handleSave() {
    const patch: Record<string, unknown> = {
      sheetsAutoSync: autoSync,
      sheetsSpreadsheetId: spreadsheetId.trim() === "" ? null : spreadsheetId.trim(),
    };
    // Only send the key when the user typed a new one — an empty box must not
    // wipe a stored key, and we never round-trip the existing one.
    if (newKey.trim() !== "") patch.sheetsSaKey = newKey.trim();
    updateSettings.mutate(patch);
    setNewKey("");
    setDirty(false);
  }

  const field: React.CSSProperties = {
    width: "100%",
    minHeight: 44,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    padding: "11px 14px",
    fontSize: 14,
    outline: "none",
    fontFamily: "'JetBrains Mono', monospace",
  };
  const connected = settings?.sheetsConnectedAs ?? null;

  return (
    <SectionCard>
      <SectionTitle>{t("sheets.title")}</SectionTitle>
      <SectionSubtitle>{t("sheets.subtitle")}</SectionSubtitle>

      {/* Connection status — never the key, only who it authenticates as. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: "10px 12px",
          borderRadius: 10,
          background: "var(--surface)",
          border: "1px solid var(--border)",
          marginBottom: 16,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            flex: "none",
            background: connected ? "var(--success, #2F8A5C)" : "var(--muted2)",
          }}
        />
        <div style={{ minWidth: 0, fontSize: 12.5 }}>
          {connected ? (
            <>
              <span style={{ color: "var(--muted)" }}>{t("sheets.connectedAs")} </span>
              <span style={{ fontFamily: "'JetBrains Mono', monospace", color: "var(--text)", wordBreak: "break-all" }}>
                {connected}
              </span>
            </>
          ) : (
            <span style={{ color: "var(--muted)" }}>{t("sheets.notConnected")}</span>
          )}
        </div>
      </div>

      {/* Auto-sync toggle */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{t("sheets.autoSyncLabel")}</div>
          <div style={{ fontSize: 12.5, color: "var(--muted)", marginTop: 2 }}>{t("sheets.autoSyncHint")}</div>
        </div>
        <Toggle
          checked={autoSync}
          label={t("sheets.autoSyncLabel")}
          onChange={(v) => {
            setAutoSync(v);
            setDirty(true);
          }}
        />
      </div>

      <FieldLabel>{t("sheets.spreadsheetIdLabel")}</FieldLabel>
      <input
        aria-label={t("sheets.spreadsheetIdLabel")}
        value={spreadsheetId}
        placeholder="1yUvLRPr…"
        onChange={(e) => {
          setSpreadsheetId(e.target.value);
          setDirty(true);
        }}
        style={{ ...field, marginBottom: 6 }}
      />
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginBottom: 16, lineHeight: 1.45 }}>
        {t("sheets.spreadsheetIdHint")}
      </div>

      <FieldLabel>{t("sheets.keyLabel")}</FieldLabel>
      <textarea
        aria-label={t("sheets.keyLabel")}
        value={newKey}
        rows={3}
        placeholder={connected ? t("sheets.keyPlaceholderSet") : t("sheets.keyPlaceholderUnset")}
        onChange={(e) => {
          setNewKey(e.target.value);
          setDirty(true);
        }}
        style={{ ...field, resize: "vertical" }}
      />
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 6, lineHeight: 1.45 }}>
        {t("sheets.keyHint")}
      </div>

      <SaveButton dirty={dirty} onClick={handleSave} label={t("sheets.save")} />
    </SectionCard>
  );
}

/**
 * Inputs for the after-tax estimate.
 *
 * The taux personnalisé is an override, not a requirement: when set it wins,
 * because it is DGFiP's own figure for the real foyer and accounts for
 * everything the estimate cannot see. Left blank, the barème estimate fills in
 * from the fields below.
 */
function TaxSection() {
  const { t } = useTranslation();
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();

  const initialized = useRef(false);
  const [situation, setSituation] = useState<string>("celibataire");
  const [regime, setRegime] = useState<string>("bnc");
  const [children, setChildren] = useState<string>("0");
  const [gardeAlternee, setGardeAlternee] = useState<string>("0");
  const [parentIsole, setParentIsole] = useState(false);
  const [spouseEuros, setSpouseEuros] = useState<string>("0");
  const [otherEuros, setOtherEuros] = useState<string>("0");
  const [tauxPerso, setTauxPerso] = useState<string>("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settings && !initialized.current) {
      initialized.current = true;
      setSituation(settings.foyerSituation ?? "celibataire");
      setRegime(settings.microRegime ?? "bnc");
      setChildren(String(settings.children ?? 0));
      setGardeAlternee(String(settings.childrenGardeAlternee ?? 0));
      setParentIsole(settings.parentIsole ?? false);
      setSpouseEuros(String((settings.spouseIncomeCents ?? 0) / 100));
      setOtherEuros(String((settings.otherIncomeCents ?? 0) / 100));
      setTauxPerso(settings.tauxPersoPct == null ? "" : String(settings.tauxPersoPct));
    }
  }, [settings]);

  function handleSave() {
    const num = (s: string) => parseFloat(s.replace(",", ".")) || 0;
    const trimmedTaux = tauxPerso.trim();
    updateSettings.mutate({
      foyerSituation: situation,
      microRegime: regime,
      children: Math.round(num(children)),
      childrenGardeAlternee: Math.round(num(gardeAlternee)),
      parentIsole,
      spouseIncomeCents: Math.round(num(spouseEuros) * 100),
      otherIncomeCents: Math.round(num(otherEuros) * 100),
      // Blank clears the override and hands the estimate back to the barème.
      tauxPersoPct: trimmedTaux === "" ? null : num(trimmedTaux),
    });
    setDirty(false);
  }

  const field: React.CSSProperties = {
    width: "100%",
    height: 44,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    padding: "0 14px",
    fontSize: 15,
    outline: "none",
  };
  const label: React.CSSProperties = {
    font: "600 11px Sora",
    letterSpacing: ".05em",
    textTransform: "uppercase",
    color: "var(--muted2)",
    marginBottom: 7,
    display: "block",
  };
  const isCouple = situation === "marie" || situation === "pacse";

  return (
    <SectionCard>
      <SectionTitle>{t("tax.title")}</SectionTitle>
      <SectionSubtitle>{t("tax.subtitle")}</SectionSubtitle>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
        <div>
          <label style={label} htmlFor="tax-taux">
            {t("tax.tauxLabel")}
          </label>
          <input
            id="tax-taux"
            inputMode="decimal"
            placeholder={t("tax.tauxPlaceholder")}
            value={tauxPerso}
            onChange={(e) => {
              setTauxPerso(e.target.value);
              setDirty(true);
            }}
            style={field}
          />
          <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 6, lineHeight: 1.45 }}>
            {t("tax.tauxHelp")}
          </div>
        </div>

        <div>
          <label style={label} htmlFor="tax-regime">
            {t("tax.regime")}
          </label>
          <select
            id="tax-regime"
            value={regime}
            onChange={(e) => {
              setRegime(e.target.value);
              setDirty(true);
            }}
            style={field}
          >
            {REGIMES.map(([v, k]) => (
              <option key={v} value={v}>
                {t(k)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={label} htmlFor="tax-situation">
            {t("tax.situation")}
          </label>
          <select
            id="tax-situation"
            value={situation}
            onChange={(e) => {
              setSituation(e.target.value);
              setDirty(true);
            }}
            style={field}
          >
            {SITUATIONS.map((v) => (
              <option key={v} value={v}>
                {t(`tax.${v}`)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={label} htmlFor="tax-children">
            {t("tax.children")}
          </label>
          <input
            id="tax-children"
            inputMode="numeric"
            value={children}
            onChange={(e) => {
              setChildren(e.target.value);
              setDirty(true);
            }}
            style={field}
          />
        </div>

        <div>
          <label style={label} htmlFor="tax-garde">
            {t("tax.gardeAlternee")}
          </label>
          <input
            id="tax-garde"
            inputMode="numeric"
            value={gardeAlternee}
            onChange={(e) => {
              setGardeAlternee(e.target.value);
              setDirty(true);
            }}
            style={field}
          />
        </div>

        {isCouple && (
          <div>
            <label style={label} htmlFor="tax-spouse">
              {t("tax.spouseIncome")}
            </label>
            <input
              id="tax-spouse"
              inputMode="decimal"
              value={spouseEuros}
              onChange={(e) => {
                setSpouseEuros(e.target.value);
                setDirty(true);
              }}
              style={field}
            />
          </div>
        )}

        <div>
          <label style={label} htmlFor="tax-other">
            {t("tax.otherIncome")}
          </label>
          <input
            id="tax-other"
            inputMode="decimal"
            value={otherEuros}
            onChange={(e) => {
              setOtherEuros(e.target.value);
              setDirty(true);
            }}
            style={field}
          />
        </div>
      </div>

      {!isCouple && (
        <label
          style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 16, cursor: "pointer" }}
        >
          <input
            type="checkbox"
            checked={parentIsole}
            onChange={(e) => {
              setParentIsole(e.target.checked);
              setDirty(true);
            }}
          />
          <span style={{ fontSize: 13.5 }}>
            {t("tax.parentIsole")}{" "}
            <span style={{ color: "var(--muted2)" }}>{t("tax.parentIsoleHint")}</span>
          </span>
        </label>
      )}

      <div
        style={{
          marginTop: 16,
          padding: "10px 12px",
          borderRadius: 10,
          background: "var(--surface)",
          border: "1px solid var(--border)",
          fontSize: 11.5,
          color: "var(--muted)",
          lineHeight: 1.5,
        }}
      >
        {t("tax.disclaimer")}{" "}
        <a
          href="https://simulateur-ir-ifi.impots.gouv.fr/calcul_impot/2026/complet/index.htm"
          target="_blank"
          rel="noreferrer"
          style={{ color: "var(--primary-deep, #D85F0E)" }}
        >
          {t("tax.officialSimulator")}
        </a>
        .
      </div>

      <SaveButton dirty={dirty} onClick={handleSave} label={t("tax.save")} />
    </SectionCard>
  );
}

/* ── Section: Échéances & déclarations ──────────────────── */
function DeadlinesSection() {
  const { t } = useTranslation();
  const { data: settings } = useSettings();
  const updateSettings = useUpdateSettings();

  const initialized = useRef(false);
  const [freq, setFreq] = useState<string>("quarterly");
  const [vl, setVl] = useState(false);
  const [tvaRegime, setTvaRegime] = useState<string>("franchise");
  const [startYear, setStartYear] = useState<string>("");
  const [tvaDay, setTvaDay] = useState<string>("17");
  const [urssafDay, setUrssafDay] = useState<string>("");
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (settings && !initialized.current) {
      initialized.current = true;
      setFreq(settings.urssafDeclarationFreq ?? "quarterly");
      setVl(settings.versementLiberatoire ?? false);
      setTvaRegime(settings.tvaRegime ?? "franchise");
      setStartYear(settings.businessStartYear == null ? "" : String(settings.businessStartYear));
      setTvaDay(String(settings.tvaDeclarationDay ?? 17));
      setUrssafDay(settings.urssafMonthlyDay == null ? "" : String(settings.urssafMonthlyDay));
    }
  }, [settings]);

  function handleSave() {
    const y = parseInt(startYear.trim(), 10);
    // Clamp the day overrides to 1–28 so no month falls short of them.
    const clampDay = (s: string): number => Math.min(28, Math.max(1, parseInt(s.trim(), 10) || 17));
    const ud = parseInt(urssafDay.trim(), 10);
    updateSettings.mutate({
      urssafDeclarationFreq: freq as "monthly" | "quarterly",
      versementLiberatoire: vl,
      tvaRegime: tvaRegime as "franchise" | "reel_simplifie" | "reel_normal",
      businessStartYear: Number.isFinite(y) ? y : null,
      tvaDeclarationDay: clampDay(tvaDay),
      urssafMonthlyDay: urssafDay.trim() === "" ? null : Math.min(28, Math.max(1, ud || 1)),
    });
    setDirty(false);
  }

  const field: React.CSSProperties = { width: "100%", height: 44, borderRadius: 10, border: "1px solid var(--border)", background: "var(--surface)", color: "var(--text)", padding: "0 14px", fontSize: 15, outline: "none" };
  const label: React.CSSProperties = { font: "600 11px Sora", letterSpacing: ".05em", textTransform: "uppercase", color: "var(--muted2)", marginBottom: 7, display: "block" };

  return (
    <SectionCard>
      <SectionTitle>{t("deadlines.title")}</SectionTitle>
      <SectionSubtitle>{t("deadlines.subtitle")}</SectionSubtitle>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14 }}>
        <div>
          <label style={label} htmlFor="dl-freq">{t("deadlines.urssafFreq")}</label>
          <select id="dl-freq" value={freq} onChange={(e) => { setFreq(e.target.value); setDirty(true); }} style={field}>
            <option value="monthly">{t("deadlines.freqMonthly")}</option>
            <option value="quarterly">{t("deadlines.freqQuarterly")}</option>
          </select>
        </div>

        <div>
          <label style={label} htmlFor="dl-tva">{t("deadlines.tvaRegime")}</label>
          <select id="dl-tva" value={tvaRegime} onChange={(e) => { setTvaRegime(e.target.value); setDirty(true); }} style={field}>
            <option value="franchise">{t("deadlines.tvaFranchise")}</option>
            <option value="reel_simplifie">{t("deadlines.tvaSimplifie")}</option>
            <option value="reel_normal">{t("deadlines.tvaNormal")}</option>
          </select>
        </div>

        <div>
          <label style={label} htmlFor="dl-year">{t("deadlines.startYear")}</label>
          <input id="dl-year" inputMode="numeric" placeholder={t("deadlines.startYearPlaceholder")} value={startYear} onChange={(e) => { setStartYear(e.target.value); setDirty(true); }} style={field} />
        </div>

        <div>
          <label style={label} htmlFor="dl-tvaday">{t("deadlines.tvaDay")}</label>
          <input id="dl-tvaday" inputMode="numeric" value={tvaDay} onChange={(e) => { setTvaDay(e.target.value); setDirty(true); }} style={field} />
        </div>

        <div>
          <label style={label} htmlFor="dl-urssafday">{t("deadlines.urssafDay")}</label>
          <input id="dl-urssafday" inputMode="numeric" placeholder={t("deadlines.urssafDayPlaceholder")} value={urssafDay} onChange={(e) => { setUrssafDay(e.target.value); setDirty(true); }} style={field} />
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", alignSelf: "end", height: 44 }}>
          <input type="checkbox" checked={vl} onChange={(e) => { setVl(e.target.checked); setDirty(true); }} style={{ width: 18, height: 18, accentColor: "var(--primary)" }} />
          <span style={{ fontSize: 14, color: "var(--text)" }}>{t("deadlines.versementLiberatoire")}</span>
        </label>
      </div>
      <div style={{ fontSize: 11.5, color: "var(--muted)", marginTop: 10, lineHeight: 1.45 }}>{t("deadlines.help")}</div>

      <SaveButton dirty={dirty} onClick={handleSave} label={t("deadlines.save")} />
    </SectionCard>
  );
}

/* ── Section: Coming soon ───────────────────────────────── */

/* ── Settings tabs (category organization per SundialSettings.dc.html) ── */

type TabId = "money" | "reminders" | "data" | "app";

const TAB_IDS: TabId[] = ["money", "reminders", "data", "app"];

const TAB_ICON: Record<TabId, React.ReactNode> = {
  money: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M15.5 9.2a4 4 0 1 0 0 5.6M8.5 11h5.5M8.5 13.2h5.5" />
    </svg>
  ),
  reminders: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  ),
  data: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5.5" rx="7.5" ry="3" />
      <path d="M4.5 5.5v13c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3v-13M4.5 12c0 1.7 3.4 3 7.5 3s7.5-1.3 7.5-3" />
    </svg>
  ),
  app: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 8h14M9 12H5m14 0h-6M5 16h14" />
      <circle cx="7" cy="8" r="1.6" />
      <circle cx="11" cy="12" r="1.6" />
      <circle cx="16" cy="16" r="1.6" />
    </svg>
  ),
};

/** Reads the initial tab from the URL hash, falling back to "money". */
function initialTab(): TabId {
  const h = (typeof window !== "undefined" ? window.location.hash : "").replace("#", "");
  return (TAB_IDS as string[]).includes(h) ? (h as TabId) : "money";
}

function SettingsTabs({ tabs }: { tabs: { id: TabId; label: string; panel: React.ReactNode }[] }) {
  const [active, setActive] = useState<TabId>(initialTab);

  function select(id: TabId) {
    setActive(id);
    try {
      window.location.hash = id;
    } catch {
      /* hash may be unavailable in a sandbox — the tab still switches */
    }
  }

  function onKey(e: React.KeyboardEvent, idx: number) {
    const n = TAB_IDS.length;
    let ni = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") ni = (idx + 1) % n;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") ni = (idx - 1 + n) % n;
    else if (e.key === "Home") ni = 0;
    else if (e.key === "End") ni = n - 1;
    if (ni < 0) return;
    e.preventDefault();
    const id = TAB_IDS[ni]!;
    select(id);
    // Move focus to the newly-selected tab (roving tabindex).
    setTimeout(() => document.getElementById(`settings-tab-${id}`)?.focus(), 0);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Segmented control — scrolls horizontally if the four labels overflow. */}
      <div
        role="tablist"
        aria-label="Settings categories"
        style={{
          display: "flex",
          gap: 6,
          overflowX: "auto",
          scrollbarWidth: "none",
          padding: 5,
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 13,
          position: "sticky",
          top: 0,
          zIndex: 5,
        }}
      >
        {tabs.map((t, idx) => {
          const on = active === t.id;
          return (
            <button
              key={t.id}
              id={`settings-tab-${t.id}`}
              role="tab"
              aria-selected={on}
              aria-controls={`settings-panel-${t.id}`}
              tabIndex={on ? 0 : -1}
              onClick={() => select(t.id)}
              onKeyDown={(e) => onKey(e, idx)}
              style={{
                flex: "1 0 auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                minHeight: 44,
                padding: "0 15px",
                whiteSpace: "nowrap",
                border: "1px solid transparent",
                borderRadius: 10,
                cursor: "pointer",
                fontFamily: "Sora",
                fontSize: 13.5,
                fontWeight: on ? 700 : 600,
                background: on ? "var(--primary)" : "transparent",
                color: on ? "#fff" : "var(--muted)",
                boxShadow: on ? "0 2px 9px var(--glow)" : "none",
                transition: ".14s",
              }}
            >
              {TAB_ICON[t.id]}
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Only the active panel renders — the whole point of the redesign. */}
      {tabs.map((t) =>
        active === t.id ? (
          <div
            key={t.id}
            id={`settings-panel-${t.id}`}
            role="tabpanel"
            aria-labelledby={`settings-tab-${t.id}`}
            tabIndex={0}
            style={{ display: "flex", flexDirection: "column", gap: 14, outline: "none" }}
          >
            {t.panel}
          </div>
        ) : null,
      )}
    </div>
  );
}

/* ── Section: Account ───────────────────────────────────── */

function ChangePasswordBlock() {
  const { t } = useTranslation();
  const changePassword = useChangePassword();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [status, setStatus] = useState<{ kind: "success" | "error"; text: string } | null>(null);

  const field: React.CSSProperties = {
    width: "100%",
    minHeight: 44,
    borderRadius: 10,
    border: "1px solid var(--border)",
    background: "var(--surface)",
    color: "var(--text)",
    padding: "11px 14px",
    fontSize: 14,
    outline: "none",
    marginBottom: 12,
  };

  async function handleSave() {
    setStatus(null);
    if (next.length < 8) {
      setStatus({ kind: "error", text: t("account.errorShort") });
      return;
    }
    try {
      await changePassword.mutateAsync({ currentPassword: current, newPassword: next });
      setCurrent("");
      setNext("");
      setStatus({ kind: "success", text: t("account.success") });
    } catch (err) {
      const code = (err as { code?: string })?.code;
      setStatus({
        kind: "error",
        text: code === "INVALID_PASSWORD" ? t("account.errorInvalid") : t("account.errorGeneric"),
      });
    }
  }

  return (
    <div style={{ marginTop: 20, paddingTop: 18, borderTop: "1px solid var(--border)" }}>
      <div style={{ font: "700 13px Sora", marginBottom: 12 }}>{t("account.changePassword")}</div>

      <FieldLabel>{t("account.currentPassword")}</FieldLabel>
      <input
        type="password"
        autoComplete="current-password"
        aria-label={t("account.currentPassword")}
        value={current}
        onChange={(e) => setCurrent(e.target.value)}
        style={field}
      />

      <FieldLabel>{t("account.newPassword")}</FieldLabel>
      <input
        type="password"
        autoComplete="new-password"
        aria-label={t("account.newPassword")}
        value={next}
        onChange={(e) => setNext(e.target.value)}
        style={field}
      />

      {status && (
        <div
          role={status.kind === "error" ? "alert" : "status"}
          style={{
            fontSize: 12.5,
            marginTop: 2,
            marginBottom: 4,
            color: status.kind === "error" ? "var(--danger, #C24141)" : "var(--success, #2F8A5C)",
          }}
        >
          {status.text}
        </div>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
        <button
          onClick={handleSave}
          disabled={changePassword.isPending || !current || !next}
          style={{
            height: 40,
            padding: "0 18px",
            border: "none",
            borderRadius: 10,
            background: current && next ? "var(--primary)" : "var(--surface)",
            color: current && next ? "#fff" : "var(--muted2)",
            font: "600 13px Sora",
            cursor: current && next && !changePassword.isPending ? "pointer" : "default",
            boxShadow: current && next ? "0 2px 10px var(--glow)" : "none",
          }}
        >
          {t("account.changePassword")}
        </button>
      </div>
    </div>
  );
}

function AccountSection() {
  const logout = useLogout();

  return (
    <SectionCard>
      <SectionTitle>Account</SectionTitle>
      <div style={{ marginTop: 14 }}>
        <button
          onClick={() => logout.mutate()}
          disabled={logout.isPending}
          style={{
            height: 38,
            padding: "0 18px",
            border: "1px solid var(--border)",
            borderRadius: 9,
            background: "none",
            color: "var(--muted)",
            font: "600 12.5px Sora",
            cursor: "pointer",
          }}
        >
          Log out
        </button>
      </div>
      <ChangePasswordBlock />
    </SectionCard>
  );
}

/* ── Settings screen ────────────────────────────────────── */

export function Settings() {
  const { t } = useTranslation();
  const [toast, setToast] = useState<string | null>(null);
  return (
    <SettingsToastCtx.Provider value={setToast}>
      <SettingsTabs
      tabs={[
        {
          id: "money",
          label: t("settingsTabs.money"),
          panel: (
            <>
              <RateSection />
              <ClientsSection />
              <UrssafSection />
              <TaxSection />
              <DeadlinesSection />
              <IdentitySection />
            </>
          ),
        },
        {
          id: "reminders",
          label: t("settingsTabs.reminders"),
          panel: (
            <>
              <RemindersSection />
              <CalendarSection />
            </>
          ),
        },
        {
          id: "data",
          label: t("settingsTabs.data"),
          panel: (
            <>
              <SheetsSection />
              <IntegrationsSection />
            </>
          ),
        },
        {
          id: "app",
          label: t("settingsTabs.app"),
          panel: (
            <>
              <AppearanceSection />
              <AccountSection />
            </>
          ),
        },
      ]}
      />
      {toast && <SettingsToast message={toast} onDismiss={() => setToast(null)} />}
    </SettingsToastCtx.Provider>
  );
}
